import { findUnknownTokens, type MeterColorKey, type MeterDefinition, MeterType, renderMeterTokens } from "@next-platform/packages-ts-types";

/** A `{{...}}` token as the Editor shows it: a chip label, its live value, or why the engine won't replace it. */
export type RulebookTokenInfo =
  | { kind: "name"; label: string }
  | { kind: "meter"; label: string; value: string; color?: MeterColorKey }
  | { kind: "unknown"; label: string; reason: string };

/** A meter's entries in the token picker; `captionToken` only when its levels have a status line to show. */
export type MeterTokenOption = {
  meterId: string;
  label: string;
  meterType: MeterType;
  color?: MeterColorKey;
  icon?: string;
  valueToken: string;
  captionToken?: string;
};

export const USER_TOKEN = "{{user}}";
export const CHAR_TOKEN = "{{char}}";

const TOKEN_RE = /\{\{([^{}]*)\}\}/g;

const TOKEN_BRACES = 2;

const hasCaption = (levels: Array<{ caption?: string }>): boolean => levels.some((level) => Boolean(level.caption?.trim()));

/**
 * Whether `.caption` has something to show for this meter: any level with a status line. For a clock this follows the
 * engine, which reads time-of-day levels when there are any and day levels otherwise.
 */
export function hasCaptionLevels(meter: MeterDefinition): boolean {
  if (meter.type === MeterType.CLOCK) {
    return hasCaption(meter.tod_levels.length > 0 ? meter.tod_levels : meter.day_levels);
  }
  return hasCaption(meter.levels);
}

/** Picker entries: every meter offers its value; captions only where a level carries one. */
export function meterTokenOptions(meters: MeterDefinition[]): MeterTokenOption[] {
  return meters.map((meter) => ({
    meterId: meter.id,
    label: meter.label,
    meterType: meter.type,
    color: meter.color,
    icon: meter.icon,
    valueToken: `{{meters.${meter.id}}}`,
    ...(hasCaptionLevels(meter) ? { captionToken: `{{meters.${meter.id}.caption}}` } : {}),
  }));
}

export const SUFFIX_LABEL: Record<string, string> = { level: "level", clock: "time", caption: "caption" };

/**
 * How the Editor shows one token, using the engine's own rules so the chip matches what the model receives:
 * `{{user}}`/`{{char}}` are names, a known meter token shows its live playtest value, anything the engine leaves
 * untouched (unknown, or a caption with no status line at this value) is flagged.
 */
export function describeRulebookToken({
  raw,
  meters,
  values,
  names,
}: {
  raw: string;
  meters: MeterDefinition[];
  values: Record<string, number>;
  names: { user: string; char: string };
}): RulebookTokenInfo {
  const inner = raw.slice(TOKEN_BRACES, -TOKEN_BRACES).trim();
  if (inner === "user") {
    return { kind: "name", label: names.user };
  }
  if (inner === "char") {
    return { kind: "name", label: names.char };
  }
  const [, meterId, suffix] = inner.split(".");
  const meter = meters.find((candidate) => candidate.id === meterId);
  if (findUnknownTokens(raw, { meters }).length > 0 || !meter) {
    return { kind: "unknown", label: raw, reason: meter ? "unknown" : "noMeter" };
  }
  const rendered = renderMeterTokens(raw, { meters }, values);
  if (rendered === raw) {
    // The engine leaves it as written (e.g. a caption token on a level with no status line).
    return { kind: "unknown", label: raw, reason: suffix === "caption" ? "noCaption" : "unknown" };
  }
  const label = suffix ? `${meter.label} ${SUFFIX_LABEL[suffix] ?? suffix}` : meter.label;
  return { kind: "meter", label, value: rendered, ...(meter.color ? { color: meter.color } : {}) };
}

/** Every token in `text` the engine won't replace, with the reason — for the warning under a field. */
export function findUnresolvedTokens({
  text,
  meters,
  values,
}: {
  text: string;
  meters: MeterDefinition[];
  values: Record<string, number>;
}): Array<{ raw: string; reason: string }> {
  const unresolved: Array<{ raw: string; reason: string }> = [];
  for (const match of text.matchAll(TOKEN_RE)) {
    const described = describeRulebookToken({ raw: match[0], meters, values, names: { user: "", char: "" } });
    if (described.kind === "unknown") {
      unresolved.push({ raw: match[0], reason: described.reason });
    }
  }
  return unresolved;
}
